import { Component, Input, Output, EventEmitter, Inject, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, FormControl, FormArray, Validators } from '@angular/forms';
import { OrganizationLocation } from '../../../modules/organization/models/organization-location';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { LocationRatesDialogComponent } from '../location-rates-dialog/location-rates-dialog.component';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, tap } from 'rxjs/operators';
import { LocationBasedRate } from '../../models/location-based-rate.model';
import { LocationDistanceRate } from '../../models/location-distance-rate.model';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'app-location-based-rates',
    templateUrl: './location-based-rates.component.html',
    styleUrls: ['./location-based-rates.component.scss'],
    standalone: false
})
export class LocationBasedRatesComponent implements OnInit {
  @Input() rateCardForm: FormGroup;
  @Input() cachedLocations: OrganizationLocation[];
  @Input() locationsNw: Observable<OrganizationLocation[]>;
  @Input() currency: string;
  @Output() locationSearch = new EventEmitter<Event>();
  @Output() locationForType = new EventEmitter<Event>();
  locationForm: FormGroup;
  locations: OrganizationLocation[];
  rateMatrix: { [locationId: string]: LocationDistanceRate[] } = {};
  distanceRateMatrix: { [locationId: string]: { [locationId: string]: LocationDistanceRate } } = {};
  filteredLocations: OrganizationLocation[] = [];

  constructor(
    private dialog: MatDialog,
    private formBuilder: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: {
      locations: Observable<OrganizationLocation[]>;
    }
  ) {
    this.locationForm = this.formBuilder.group({
      locationForType: ['', Validators.required]
    });
  }

  ngOnInit() {
    if (!this.rateCardForm) {
      this.rateCardForm = this.formBuilder.group({
        locationRates: this.formBuilder.array([]),
        locationApplyType: ['NEAREST'],
        distanceLessThan: [null, Validators.required],
        rateLessThan: [null, Validators.required],
        distanceGreaterThan: [null, Validators.required],
        rateGreaterThan: [null, Validators.required],
        locationBasedRate: [null]
      });
    } else {
      if (!this.rateCardForm.contains('locationRates')) {
        this.rateCardForm.addControl('locationRates', this.formBuilder.array([]));
      }
      if (!this.rateCardForm.contains('locationApplyType')) {
        this.rateCardForm.addControl('locationApplyType', new FormControl('NEAREST'));
      }
      if (!this.rateCardForm.contains('distanceLessThan')) {
        this.rateCardForm.addControl('distanceLessThan', new FormControl(null, Validators.required));
      } else {
        const control = this.rateCardForm.get('distanceLessThan');
        if (control && !control.hasValidator(Validators.required)) {
          control.setValidators(Validators.required);
          control.updateValueAndValidity();
        }
      }
      if (!this.rateCardForm.contains('rateLessThan')) {
        this.rateCardForm.addControl('rateLessThan', new FormControl(null, Validators.required));
      } else {
        const control = this.rateCardForm.get('rateLessThan');
        if (control && !control.hasValidator(Validators.required)) {
          control.setValidators(Validators.required);
          control.updateValueAndValidity();
        }
      }
      if (!this.rateCardForm.contains('distanceGreaterThan')) {
        this.rateCardForm.addControl('distanceGreaterThan', new FormControl(null, Validators.required));
      } else {
        const control = this.rateCardForm.get('distanceGreaterThan');
        if (control && !control.hasValidator(Validators.required)) {
          control.setValidators(Validators.required);
          control.updateValueAndValidity();
        }
      }
      if (!this.rateCardForm.contains('rateGreaterThan')) {
        this.rateCardForm.addControl('rateGreaterThan', new FormControl(null, Validators.required));
      } else {
        const control = this.rateCardForm.get('rateGreaterThan');
        if (control && !control.hasValidator(Validators.required)) {
          control.setValidators(Validators.required);
          control.updateValueAndValidity();
        }
      }
      if (!this.rateCardForm.contains('locationBasedRate')) {
        this.rateCardForm.addControl('locationBasedRate', new FormControl(null));
      }

      this.locationsNw.pipe(
        tap(lcs => {
          this.locations = lcs;
        })
      ).subscribe();

      const locationBasedRate = this.rateCardForm.get('locationBasedRate')?.value;
      if (locationBasedRate?.distanceRateMatrix) {
        this.distanceRateMatrix = locationBasedRate.distanceRateMatrix;
      }

      const locations = locationBasedRate?.distanceRateMatrix ? Object.keys(locationBasedRate.distanceRateMatrix) : [];
      this.filteredLocations = this.locations.filter(location => locations.includes(location.id));
      this.filteredLocations = this.removeDuplicateLocations(this.filteredLocations);

      if (locationBasedRate?.location && this.locations) {
        const existingLocation = this.locations.find(loc => loc.id === locationBasedRate.location);
        if (existingLocation) {
          this.locationForm.patchValue({
            locationForType: existingLocation
          });
        }
      }
    }
    this.rateCardForm.get('distanceLessThan').valueChanges.subscribe(value => {
      if (value === null || value === '') {
        this.rateCardForm.patchValue({
          distanceGreaterThan: null
        }, { emitEvent: false });
      } else if (value !== null && value !== undefined) {
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (!isNaN(numValue)) {
          const formattedValue = numValue.toFixed(2);
          const otherValue = this.rateCardForm.get('distanceGreaterThan').value;
          if (formattedValue !== otherValue?.toString()) {
            this.rateCardForm.patchValue({
              distanceGreaterThan: parseFloat(formattedValue)
            }, { emitEvent: false });
          }
        }
      }
    });
    this.rateCardForm.get('distanceGreaterThan').valueChanges.subscribe(value => {
      if (value === null || value === '') {
        this.rateCardForm.patchValue({
          distanceLessThan: null
        }, { emitEvent: false });
      } else if (value !== null && value !== undefined) {
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (!isNaN(numValue)) {
          const formattedValue = numValue.toFixed(2);
          const otherValue = this.rateCardForm.get('distanceLessThan').value;
          if (formattedValue !== otherValue?.toString()) {
            this.rateCardForm.patchValue({
              distanceLessThan: parseFloat(formattedValue)
            }, { emitEvent: false });
          }
        }
      }
    });
    this.rateCardForm.get('currencyCode').valueChanges.subscribe(value => {
      this.currency = value;
    });

    this.rateCardForm.valueChanges.subscribe(() => {
      this.updateLocationBasedRate();
    });
    this.locationForm.valueChanges.subscribe(() => {
      this.updateLocationBasedRate();
    });
  }

  openCreateLocation() {
    const locationBasedRate = this.rateCardForm.get('locationBasedRate')?.value;
    const existingLocationRates = this.convertToDistanceRateArray(locationBasedRate?.distanceRateMatrix);
    let matrix = {};
    if (this.rateMatrix !== undefined && this.rateMatrix !== null && Object.keys(this.rateMatrix).length > 0) {
      matrix = this.rateMatrix;
    } else {
      matrix = existingLocationRates;
    }
    const dialogRef = this.dialog.open(LocationRatesDialogComponent, {
      panelClass: 'location-rates-dialog-container',
      data: {
        locationRates: this.rateCardForm.get('locationRates')?.value || [],
        locations: this.locations,
        distanceRateMatrix: matrix
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const locationId = Object.keys(result)[0];
        const rates = result[locationId];
        this.rateMatrix = result;
        this.distanceRateMatrix = this.convertDistanceRateMatrix(result);
        const locations = Object.keys(this.distanceRateMatrix);
        this.filteredLocations = this.locations.filter(location => locations.includes(location.id));
        this.filteredLocations = this.removeDuplicateLocations(this.filteredLocations);
        const locationRatesArray = this.rateCardForm.get('locationRates') as FormArray;
        locationRatesArray.clear();
        rates.forEach(rate => {
          locationRatesArray.push(this.formBuilder.group({
            location: [rate.location],
            distance: [rate.distance],
            rate: [rate.rate]
          }));
        });
        const selectedLocation = this.locations.find(loc => loc.id === locationId);
        if (selectedLocation) {
          this.locationForm.patchValue({
            location: selectedLocation
          });
        }
        this.updateLocationBasedRate();
      }
    });
  }

  convertDistanceRateMatrix(result: { [locationId: string]: LocationDistanceRate[] }) {
    const convertedMatrix: { [locationId: string]: { [locationId: string]: LocationDistanceRate } } = {};
    Object.keys(result).forEach(locationId => {
      const location = this.locations.find(loc => loc.referenceId === locationId);
      convertedMatrix[location?.id] = {};
      result[locationId].forEach(rate => {
        const otherLocation = this.locations.find(loc => loc.referenceId === rate.locationId)
           || this.locations.find(loc => loc.locationName === rate.locationId);
        const locationDistanceRate: LocationDistanceRate = {
          locationId: otherLocation.id,
          locationRefId: otherLocation.referenceId,
          distance: rate.distance,
          rate: rate.rate
        };
        convertedMatrix[location.id][otherLocation.id] = locationDistanceRate;
      });
    });
    return convertedMatrix;
  }

  convertToDistanceRateArray(matrix: { [locationId: string]: { [locationId: string]: LocationDistanceRate } }) {
    if (!matrix) {
      return {};
    }
    const result: { [locationId: string]: LocationDistanceRate[] } = {};
    Object.keys(matrix).forEach(locationId => {
      const location = this.locations.find(loc => loc.id === locationId);
      if (location) {
        const rates: LocationDistanceRate[] = [];
        Object.values(matrix[locationId]).forEach(rate => {
          const otherLocation = this.locations.find(loc => loc.id === rate.locationId);
          if (otherLocation) {
            rates.push({
              locationId: otherLocation.referenceId,
              locationRefId: otherLocation.referenceId,
              distance: rate.distance,
              rate: rate.rate
            });
          }
        });
        result[location.referenceId] = rates;
      }
    });
    return result;
  }

  displayLocationName(location: OrganizationLocation): string {
    return location ? location.locationName : '';
  }

  onLocationSearch(event: Event) {
    this.locationSearch.emit(event);
  }

  onLocationForType(event: Event) {
    const matrixLocationIds = Object.keys(this.distanceRateMatrix);
    this.filteredLocations = this.locations.filter(location => matrixLocationIds.includes(location.id));
    const searchValue = (event.target as HTMLInputElement).value.toLowerCase();
    if (this.filteredLocations) {
      this.filteredLocations = this.filteredLocations.filter(location =>
        location.locationName.toLowerCase().includes(searchValue));
      this.filteredLocations = this.removeDuplicateLocations(this.filteredLocations);
    }
  }

  private removeDuplicateLocations(locations: OrganizationLocation[]): OrganizationLocation[] {
    const uniqueLocationsMap = new Map();
    locations.forEach(location => {
      if (!uniqueLocationsMap.has(location.id)) {
        uniqueLocationsMap.set(location.id, location);
      }
    });
    return Array.from(uniqueLocationsMap.values());
  }

  markLocationFormAsTouched() {
    this.locationForm.markAllAsTouched();
    this.rateCardForm.markAllAsTouched();
  }

  private updateLocationBasedRate() {
    const formValue = this.rateCardForm.value;
    const currentLocationBasedRate = this.rateCardForm.get('locationBasedRate')?.value;

    const locationBasedRate: LocationBasedRate = {
      distanceRateMatrix: this.distanceRateMatrix,
      minRate: formValue.rateLessThan,
      maxRate: formValue.rateGreaterThan,
      minDistanceRef: formValue.distanceLessThan,
      maxDistanceRef: formValue.distanceGreaterThan,
      locationApplyType: formValue.locationApplyType,
      location: this.locationForm.get('locationForType')?.value?.id || ''
    };

    this.rateCardForm.patchValue({
      locationBasedRate
    }, { emitEvent: false });

    if (this.rateCardForm.parent) {
      const parentForm = this.rateCardForm.parent as FormGroup;
      if (!parentForm.contains('locationBasedRate')) {
        parentForm.addControl('locationBasedRate', new FormControl(null));
      }
      parentForm.patchValue({
        locationBasedRate
      }, { emitEvent: false });
    }
  }
}
